import { useQuery } from '@tanstack/react-query'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectExpenseReportIdFromRoute,
  selectReceiptAttachmentModelsList,
  selectUploadingAttachmentsFromState,
} from 'src/app/AddExpenseFlyout/redux/addExpense.selectors'
import { createSetExpenseReceiptAttachmentIds } from 'src/app/UpdateExpenseFlyout/redux/updateExpense.actions'
import ExpenseFlyoutHeader from 'src/components/ExpenseFlyoutHeader/ExpenseFlyoutHeader'
import ExpenseForm, {
  ExpenseFields,
} from 'src/components/ExpenseForm/ExpenseForm'
import LoadingRightFlyoutContent from 'src/components/LoadingRightFlyoutContent/LoadingRightFlyoutContent'
import NotFoundRightFlyoutContent from 'src/components/NotFoundRightFlyoutContent/NotFoundRightFlyoutContent'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import { ExtendCurrency } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  ExpenseReportModel,
  ExpenseReportStatus,
} from 'src/models/expenseReport.model'
import { expenseReportUrl } from 'src/navigation/urls'
import { expenseReportsQueries } from 'src/queries/expenseReports/expenseReports.queries'
import { useCreateExpense } from 'src/queries/expenses.mutations'
import { recoverBackendErrorPayload } from 'src/queries/network.utils'
import {
  isOutOfPocketExpensesVisible,
  orgOutOfPocketExpensesQueries,
} from 'src/queries/organizations/outOfPocketExpenses/outOfPocketExpenses.queries'
import { useUploadReceiptAttachments } from 'src/queries/receiptAttachments/receiptAttachments.queries'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { toCents, toDollars } from 'src/utils/currency.utils'
import { parseBackendErrors } from 'src/utils/error.utils'

const AddExpenseFlyout: React.FC = () => {
  const translate = useTranslate()

  const dispatch = useFlyoutDispatch()
  const uploadReceiptAttachments = useUploadReceiptAttachments()
  const expenseReportId = useSelector(selectExpenseReportIdFromRoute)
  const reportQuery = useQuery(
    expenseReportsQueries.reportDetail(expenseReportId),
  )
  const expenseReport = useMemo(
    () =>
      reportQuery.data !== undefined
        ? new ExpenseReportModel(reportQuery.data)
        : undefined,
    [reportQuery.data],
  )
  const fetchFailure = reportQuery.isError
  const createExpenseMutation = useCreateExpense()
  const postingExpense = createExpenseMutation.isPending
  // Recovers the same backend payload the addExpense reducer expects (parseBackendErrors) from the normalized mutation error — response-safe for network failures.
  const backendError = createExpenseMutation.error
    ? parseBackendErrors(
        recoverBackendErrorPayload(createExpenseMutation.error),
      )
    : undefined
  const errorDetailMap = backendError?.errorDetailMap
  const topLevelError = backendError?.topLevelError
  const receiptAttachments = useSelector(selectReceiptAttachmentModelsList)
  const uploadingAttachmentThumbnails = useSelector(
    selectUploadingAttachmentsFromState,
  )
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { data: hasOutOfPocketExpenses } = useQuery(
    orgOutOfPocketExpensesQueries.enabled(organization?.id),
  )
  const outOfPocketExpensesEnabled = isOutOfPocketExpensesVisible(
    organization,
    hasOutOfPocketExpenses,
  )
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const isExpenseReportPage =
    matchPath(
      calculatedBackgroundLocation.pathname,
      expenseReportUrl(expenseReportId),
    ) !== null
  const { analytics } = useAnalyticsContext()

  const dispatchUploadAttachments = (
    attachments: PostReceiptAttachmentRequest[],
  ): void => {
    uploadReceiptAttachments.mutate(attachments)
  }

  const deleteAttachment = (attachmentId: string): void => {
    dispatch(
      createSetExpenseReceiptAttachmentIds(
        receiptAttachments
          .filter((att) => att.id !== attachmentId)
          .map((att) => att.id),
      ),
    )
  }

  const handleScroll = (): void => {
    document.getElementById('flyout')?.scrollIntoView()
  }

  const dispatchPostCreateExpense = (
    body: ExpenseFields,
    amountToBeReimbursed: number,
    isExpenseTypeMileage: boolean,
  ): void => {
    const payload: Record<string, unknown> = {
      ...body,
      amountCents: isExpenseTypeMileage
        ? toCents(amountToBeReimbursed)
        : body.amountCents !== undefined
          ? toCents(body.amountCents)
          : undefined,
      expenseReportId: expenseReportId,
      currencyCode: ExtendCurrency.Usd,
      receiptAttachmentIds: receiptAttachments.map((att) => att.id),
      mileageRate: isExpenseTypeMileage
        ? (organization?.mileageRateThousandths ?? 0) * 1000
        : undefined,
    }
    if (isExpenseTypeMileage) {
      payload.useMileageRoute = body.useMileageRoute ?? false
      if (body.useMileageRoute) {
        payload.distance = null
      } else {
        payload.mileageRoute = null
      }
    }
    createExpenseMutation.mutate(payload as ExpenseFields, {
      onSuccess: () => {
        isExpenseReportPage &&
          analytics?.track(
            'out of pocket expense report details page - add expense - button press',
            {
              interaction: 'button press',
              intent: 'add expense',
              Expense_Name: payload.expenseDate,
              Amount_Spent: toDollars((payload.amountCents as number) ?? 0),
              Expense_Date: payload.expenseDate,
              Receipt_Uploaded: payload.receiptAttachmentIds
                ? (payload.receiptAttachmentIds as string[]).length > 0
                : false,
            },
          )
      },
    })
    analytics?.track('Successfully create OOPE', {
      expense_type: body.type === 'MILEAGE' ? 'Mileage' : 'General',
      amount_spent: body.amountCents ? body.amountCents : undefined,
      distance: body.distance ?? body.mileageRoute?.distance,
      expense_name: expenseReport?.reportName,
      receipt_attached: receiptAttachments && receiptAttachments.length > 0,
      add_notes: !!body.notes,
      ...(isExpenseTypeMileage && {
        mileage_calculation_method: body.useMileageRoute
          ? 'google_maps'
          : 'manual',
      }),
      ...(isExpenseTypeMileage &&
        body.useMileageRoute && {
          stops_count: body.mileageRoute?.stops?.length ?? 0,
        }),
    })
  }

  return (
    <RightFlyout width={RightFlyoutBodyWidths.Action}>
      {outOfPocketExpensesEnabled &&
      expenseReport !== undefined &&
      expenseReport.status === ExpenseReportStatus.Draft ? (
        <>
          <span id={'flyout'}>
            <ExpenseFlyoutHeader
              reportName={expenseReport.reportName}
              flyoutTitle={'Add New Expense'}
            />
            <ExpenseForm
              expenseReportId={expenseReportId}
              isSubmitter={true}
              handleScroll={handleScroll}
              loading={postingExpense}
              deleteAttachment={deleteAttachment}
              errorDetailMap={errorDetailMap}
              receiptAttachments={receiptAttachments}
              uploadAttachments={dispatchUploadAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              topLevelError={topLevelError}
              buttonText={translate('Save and Review')}
              notesAndReceiptsEditable
              fieldsEditable
              mileageRate={organization?.mileageRateThousandths}
              onSuccessfulSubmit={dispatchPostCreateExpense}
            />
          </span>
        </>
      ) : fetchFailure ? (
        <NotFoundRightFlyoutContent />
      ) : (
        <LoadingRightFlyoutContent />
      )}
    </RightFlyout>
  )
}

export default AddExpenseFlyout
